import { useNavigate } from '@tanstack/react-router';
import {
  createColumnHelper,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { Suspense } from 'react';

import { GenericTable } from '../../../components/ui/generic-table';
import { useIngredientRecipes } from '../api/ingredients-queries';
import { IngredientEditSectionTitle } from './ingredient-edit-section-title';

type IngredientRecipe = ReturnType<typeof useIngredientRecipes>['data'][number];

interface IngredientRecipeTablePresentationProps {
  ingredientId: string;
  organisationId: string;
}

const columnHelper = createColumnHelper<IngredientRecipe>();

const columns = [
  columnHelper.accessor('recipe.title', {
    id: 'recipe',
    header: 'Recipe',
    cell: (info) => <span className="font-medium">{info.getValue()}</span>,
    size: 60,
  }),
  columnHelper.accessor('latest_recipe_ingredient_quantity', {
    id: 'quantity',
    header: 'Quantity Used',
    cell: (info) => {
      const quantities = info.getValue();
      const quantity = quantities.length > 0 ? quantities[0] : null;
      if (!quantity) return '-';
      return `${quantity.end_quantity.toFixed(2)} ${quantity.unit}`;
    },
    size: 30,
  }),
];

function IngredientRecipeTablePresentation({
  ingredientId,
  organisationId,
}: IngredientRecipeTablePresentationProps) {
  const navigate = useNavigate({
    from: '/$tenantId/ingredients/$ingredientId',
  });

  const { data: ingredientRecipes } = useIngredientRecipes(
    ingredientId,
    organisationId,
  );

  const table = useReactTable({
    data: ingredientRecipes,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  const handleRowClick = async (row: IngredientRecipe) => {
    await navigate({
      to: '/$tenantId/recipes/$recipeId',
      params: (params) => ({
        ...params,
        recipeId: row.recipe_id,
      }),
    });
  };

  return (
    <GenericTable
      table={table}
      emptyMessage="This ingredient is not used in any recipes yet"
      containerClassName="rounded-md border overflow-hidden"
      onClick={handleRowClick}
    />
  );
}

interface IngredientRecipeTableProps {
  ingredientId: string;
  organisationId: string;
  isEditable: boolean;
}

export function IngredientRecipeTable({
  ingredientId,
  organisationId,
  isEditable,
}: IngredientRecipeTableProps) {
  const table = useReactTable({
    data: [],
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  return (
    <>
      <div className="flex justify-between items-center">
        <IngredientEditSectionTitle>Recipes</IngredientEditSectionTitle>
        {/* {isEditable && (
          // // TODO: Make all modals like this where the trigger is included in the component, removing need for us to manage this state?
          // <AddIngredientModal onAddRecipeIngredient={addRecipeIngredient} />
        )} */}
      </div>
      <Suspense
        fallback={
          <GenericTable table={table} loading={true} skeletonRows={5} />
        }
      >
        <IngredientRecipeTablePresentation
          ingredientId={ingredientId}
          organisationId={organisationId}
        />
      </Suspense>
    </>
  );
}
